import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { PageBaseInternal } from '../../../../webmodule-common/other/ui/pages/page-base';
import { pages } from '../../page-constants';
import { PreventAndRedirectCommands, Router, RouterLocation } from '@vaadin/router';
import { urlForName } from '../../../../webmodule-common/other/router';
import { userDataStore } from '../../common/current-user-data-store';

/**
 * Dealer Portal page base. Requires a usable branch before most routes mount.
 * Login, no-branches, and Settings (Staff Admin / support recovery) opt out
 * via `requiresAvailableBranches()`. That check runs after core details load
 * so claim-based overrides see franchisee-admin / staff / supplier claims.
 */
export class PageBase extends PageBaseInternal {
  protected defaultTitleTemplate = `%s | Dealer's Module`;

  async loadUserCoreDetails(): Promise<void> {
    await userDataStore.loadCoreDetails();
  }

  /** Override to false on pages that must remain reachable without a usable branch. */
  protected requiresAvailableBranches(): boolean {
    return true;
  }

  async onBeforeEnter(location: RouterLocation, commands: PreventAndRedirectCommands, router: Router): Promise<any> {
    // Load claims first so claim-based requiresAvailableBranches() overrides
    // (Settings recovery) see franchisee-admin / staff / supplier. Login has
    // no user yet; no-branches still loads, then skips the redirect.
    if (getCurrentUser()) {
      try {
        await this.loadUserCoreDetails(); // MultiPromise — no extra network once loaded
        if (this.requiresAvailableBranches() && userDataStore.loaded && !userDataStore.hasAvailableBranches()) {
          return commands.redirect(urlForName(pages.noBranches));
        }
      } catch {
        // Transient load failure should not block navigation; page can retry/degrade.
      }
    }

    return await super.onBeforeEnter(location, commands, router);
  }
}

export abstract class TabbedPageBase extends PageBase {
  protected async awaken() {
    await this.setActiveTabByHash();
    await super.awaken();
  }
  // eslint-disable-next-line @typescript-eslint/require-await
  protected abstract setActiveTabByHash(): Promise<void>;
  protected async afterUserConnected() {
    await this.setActiveTabByHash();
  }
}
